import { useEffect, useRef, useState } from "react";
import Editor, { loader } from "@monaco-editor/react";

const MONACO_LANG = { python: "python", javascript: "javascript", bash: "shell", c: "c", cpp: "cpp" };
const OPTIONS = {
  fontSize: 14, fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontLigatures: true,
  minimap: { enabled: false }, scrollBeyondLastLine: false, automaticLayout: true, tabSize: 4,
  autoIndent: "full", autoClosingBrackets: "always", autoClosingQuotes: "always", autoSurround: "languageDefined",
  bracketPairColorization: { enabled: true }, guides: { bracketPairs: true, indentation: true }, matchBrackets: "always",
  renderLineHighlight: "all", smoothScrolling: true, cursorBlinking: "smooth", padding: { top: 16, bottom: 16 },
  // Autocomplete is always off for assessment fairness.
  quickSuggestions: false, suggestOnTriggerCharacters: false, wordBasedSuggestions: "off", parameterHints: { enabled: false },
  inlineSuggest: { enabled: false }, snippetSuggestions: "none", tabCompletion: "off", acceptSuggestionOnEnter: "off",
  hover: { enabled: false }, codeLens: false, lightbulb: { enabled: "off" }, contextmenu: false,
};

const useNarrow = () => {
  const q = "(max-width: 767px), (pointer: coarse)";
  const [narrow, setNarrow] = useState(() => window.matchMedia(q).matches);
  useEffect(() => { const m = window.matchMedia(q); const f = () => setNarrow(m.matches); m.addEventListener("change", f); return () => m.removeEventListener("change", f); }, []);
  return narrow;
};

// VS Code-style editor on desktop; plain textarea on phones/tablets or if the editor engine fails to load.
export function CodeEditor({ value, language, onChange, onRun, pasteGuard, selectionRef }) {
  const narrow = useNarrow();
  const [failed, setFailed] = useState(false);
  const runRef = useRef(onRun);
  runRef.current = onRun;
  useEffect(() => { if (!narrow) loader.init().catch(() => setFailed(true)); }, [narrow]);

  if (narrow || failed) {
    return (
      <textarea data-testid="code-editor" value={value} onChange={(e) => onChange(e.target.value)} {...pasteGuard}
        spellCheck={false} className="tp-editor flex-1 w-full p-5 resize-none border-0" style={{ minHeight: 300 }} />
    );
  }
  const beforeMount = (monaco) => {
    const off = { completionItems: false, signatureHelp: false, hovers: false, codeActions: false, inlayHints: false, documentHighlights: true, diagnostics: true };
    monaco.languages.typescript?.javascriptDefaults.setModeConfiguration?.(off);
    monaco.languages.typescript?.javascriptDefaults.setDiagnosticsOptions?.({ noSemanticValidation: true, noSuggestionDiagnostics: true });
  };
  const onMount = (editor, monaco) => {
    const { KeyMod: M, KeyCode: K } = monaco;
    // Block shortcuts that open suggestions, hints or the command palette.
    [M.CtrlCmd | K.Space, M.WinCtrl | K.Space, M.CtrlCmd | K.KeyI, M.CtrlCmd | M.Shift | K.Space, K.F1, M.CtrlCmd | M.Shift | K.KeyP, M.Alt | K.Escape]
      .forEach((kb) => editor.addCommand(kb, () => {}));
    selectionRef.current = () => editor.getModel()?.getValueInRange(editor.getSelection()) || "";
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => runRef.current?.());
    editor.focus();
  };
  return (
    <div className="flex-1 min-h-[300px] relative" data-testid="code-editor-monaco" data-language={MONACO_LANG[language] || "plaintext"}
      onCopyCapture={pasteGuard.onCopy} onCutCapture={pasteGuard.onCut} onPasteCapture={pasteGuard.onPaste}>
      <Editor height="100%" theme="vs-dark" language={MONACO_LANG[language] || "plaintext"} value={value}
        onChange={(v) => onChange(v ?? "")} beforeMount={beforeMount} onMount={onMount} options={OPTIONS}
        loading={<div className="p-5 text-sm text-slate-500 font-mono">Loading editor…</div>} />
    </div>
  );
}
